import styled, { css } from 'styled-components';
import { hexToRgb } from '@/utilities/colors';

export interface Props {
  light?: boolean;
  noTextWrap?: boolean;
  columnWidths?: string;
  columnTextAlignments?: string;
}

const tone = css<Props>`
  ${({ theme, light }) =>
    light ? hexToRgb(theme.color.spaceGray) : hexToRgb(theme.color.white)}
`;

export default styled('table')<Props>`
  width: 100%;
  table-layout: auto;
  border-spacing: 0;
  border: 1px solid rgba(${tone}, 0.2);
  color: ${({ theme, light }) =>
    light ? theme.color.spaceGray : theme.color.white};

  caption {
    ${({ theme }) => theme.typography.tiny}
    opacity: 0.5;
    padding: ${({ theme }) => theme.x1spacePx}px;
    caption-side: bottom;
    text-align: right;
  }

  thead tr th {
    background: rgba(${tone}, 0.1);
    ${({ theme }) => theme.typography.xSmallStrong};
    border: none;
  }

  tbody tr {
    ${({ theme }) => theme.typography.xSmall};
    &:nth-of-type(odd) {
      background: transparent;
    }
  }

  tr {
    height: ${({ theme }) => theme.x5spacePx}px;
  }

  th,
  td {
    ${({ noTextWrap }) => noTextWrap && `white-space: nowrap;`}
    padding: ${({ theme }) => theme.x1spacePx}px;
    text-align: left;
    border-top: 1px solid rgba(${tone}, 0.2);
    vertical-align: top;

    &:last-child {
      text-align: right;
    }

    ${({ columnWidths }) => {
      if (columnWidths && columnWidths.length) {
        const columnWidthsArray = columnWidths
          .split(',')
          .map((n: string) => +n)
          .reduce((a: number[], b: number) => [...a, isNaN(b) ? 0 : b], []);
        const columnWidthsTotal = columnWidthsArray.reduce((a, b) => a + b, 0);
        return columnWidthsArray.map(
          (n: number, i: number) => css`
            &:nth-child(${i + 1}) {
              width: ${(n / columnWidthsTotal) * 100}%;
            }
          `
        );
      }
    }};

    ${({ columnTextAlignments }) => {
      if (columnTextAlignments && columnTextAlignments.length) {
        const columnTextAlignmentsArray = columnTextAlignments
          .split(',')
          .map((n) => n.trim());
        return columnTextAlignmentsArray.map(
          (n: string, i: number) => css`
            &:nth-child(${i + 1}) {
              text-align: ${n === 'l'
                ? 'left'
                : n === 'r'
                  ? 'right'
                  : 'center'};
            }
          `
        );
      }
    }};
  }

  //Remove duplicate border when not using table header
  > * {
    &:first-child tr:first-child td {
      border-top: none;
    }
  }
`;
